<script setup lang="ts">
import {computed} from 'vue'
import {useStore} from 'vuex'
import {formatTime} from '@/utils/datetime'
import type {ReportTemplate} from '@/types'
import {NotepadTextDashed} from 'lucide-vue-next'
import {Badge} from '@/components/ui/badge'

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])

const props = defineProps<{
  template: ReportTemplate
}>()

const getGeometryBadgeVariant = (geometryType: string) => {
  switch(geometryType?.toLowerCase()) {
    case 'point':
      return 'default'
    case 'linestring':
      return 'secondary'
    case 'polygon':
      return 'outline'
    default:
      return 'outline'
  }
}
</script>

<template>
  <div class="space-y-4">
    <!-- Icon and Name -->
    <div class="flex items-start gap-4">
      <div class="flex-shrink-0">
        <NotepadTextDashed v-if="!template.s3_icon" class="w-12 h-12 text-gray-400" />
        <img v-else :src="template.s3_icon" alt="Template icon" class="w-12 h-12 object-contain rounded" />
      </div>
      <div class="flex-1 min-w-0">
        <h3 class="text-lg font-semibold text-gray-900">{{ template.name }}</h3>
        <div class="mt-1 flex items-center gap-2">
          <Badge :variant="getGeometryBadgeVariant(template.geometry_type)">
            {{ template.geometry_type }}
          </Badge>
          <div
            class="w-4 h-4 rounded-full border border-gray-200"
            :style="`background-color: ${template.color}`"
            :title="`Color: ${template.color}`"
          ></div>
        </div>
      </div>
    </div>

    <!-- Description -->
    <div v-if="template.description">
      <h4 class="text-sm font-medium text-gray-500 mb-1">Description</h4>
      <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ template.description }}</p>
    </div>
    <div v-else>
      <p class="text-sm text-gray-400 italic">No description provided</p>
    </div>

    <!-- Metadata -->
    <div class="pt-2 border-t border-gray-100">
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span class="text-gray-500">Created</span>
          <p class="text-gray-700">{{ formatTime(template.created_at, settings?.tz || 'UTC', 'D MMMM YYYY') }}</p>
        </div>
        <div>
          <span class="text-gray-500">Image Required</span>
          <p class="text-gray-700">{{ template.image_required ? 'Yes' : 'No' }}</p>
        </div>
      </div>
    </div>

    <!-- Data Fields -->
    <div v-if="template.data_fields && template.data_fields.length > 0" class="pt-2 border-t border-gray-100">
      <h4 class="text-sm font-medium text-gray-500 mb-2">Data Fields ({{ template.data_fields.length }})</h4>
      <div class="space-y-1">
        <div
          v-for="field in template.data_fields"
          :key="field.id"
          class="flex items-center justify-between text-sm py-1 px-2 bg-gray-50 rounded"
        >
          <span class="text-gray-700">{{ field.key }}</span>
          <span class="text-gray-400 text-xs">
            {{ ['Text', 'Number', 'Yes/No', 'Multi-line', 'Choices'][field.type] || 'Unknown' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
